import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { Button } from './ui/Button';
import TextInput from './ui/TextInput';
import { Label } from './ui/Label';
import { cn } from '../lib/utils';
import { APP_BRAND } from '../config/branding';
import AppBrandIcon from './branding/AppBrandIcon';

interface Login2FAProps {
  pendingToken: string;
  onBack: () => void;
}

const Login2FA: React.FC<Login2FAProps> = ({ pendingToken, onBack }) => {
  const [code, setCode] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { colors } = useTheme();
  const { verify2fa } = useAuth();
  const { appConfig } = useSettings();
  const { t } = useTranslation('login');
  const productName = appConfig.system.appName?.trim() || APP_BRAND.name;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      await verify2fa({
        pendingToken,
        code: useRecovery ? undefined : code,
        recoveryCode: useRecovery ? recoveryCode : undefined,
      });
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      setError(message || t('invalidCode'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={cn('flex flex-1 items-center justify-center px-4 py-10 sm:px-8', colors.background)}>
      <div className="w-full max-w-md">
        <div className={cn('rounded-2xl border p-8 shadow-sm', colors.surface, colors.border)}>
          <div className="mb-6 flex items-center gap-3">
            <AppBrandIcon className="h-10 w-10" iconClassName="h-5 w-5" />
            <div>
              <h2 className={cn('text-xl font-semibold', colors.text)}>{t('twoFactorTitle')}</h2>
              <p className={cn('text-sm', colors.textMuted)}>{t('twoFactorSubtitle')}</p>
            </div>
          </div>

          <form className="space-y-5" onSubmit={handleSubmit}>
            {error && (
              <div className={cn('rounded-lg border px-4 py-3 text-sm', colors.error)} role="alert">
                {error}
              </div>
            )}

            {!useRecovery ? (
              <div>
                <Label htmlFor="code">{t('authCode')}</Label>
                <TextInput
                  id="code"
                  name="code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="000000"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="mt-1.5 tracking-widest"
                  maxLength={6}
                  required
                />
              </div>
            ) : (
              <div>
                <Label htmlFor="recoveryCode">{t('recoveryCode')}</Label>
                <TextInput
                  id="recoveryCode"
                  name="recoveryCode"
                  type="text"
                  placeholder={t('recoveryCodePlaceholder')}
                  value={recoveryCode}
                  onChange={(e) => setRecoveryCode(e.target.value)}
                  className="mt-1.5"
                  required
                />
              </div>
            )}

            <Button type="submit" className="w-full" loading={isLoading} disabled={isLoading}>
              {t('verifyButton', { productName })}
            </Button>

            <div className="flex flex-col gap-2 text-center text-sm">
              <button
                type="button"
                className="text-indigo-600 hover:text-indigo-700"
                onClick={() => {
                  setUseRecovery(!useRecovery);
                  setError('');
                }}
              >
                {useRecovery ? t('useAuthCodeInstead') : t('useRecoveryInstead')}
              </button>
              <button type="button" className={cn(colors.textMuted, 'hover:underline')} onClick={onBack}>
                {t('backToSignIn')}
              </button>
              <Link to="/forgot-password" className="text-xs text-indigo-600 hover:text-indigo-700">
                {t('forgotPassword')}
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login2FA;
